import * as React from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { FilterSelect } from "@/pages/areas/components/filter-select";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import { useReloadAreas } from "@/pages/areas/hooks/use-reload-areas";
import { runBatches } from "@/pages/areas/lib/run-batches";
import type { Area } from "@/types/area";

const BATCH = 50;

export interface MergeAreaDialogProps {
  source: {
    id: string;
    name: string;
    city: string;
    countryCode: string;
    venueCount: number;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the target id after a merge. Without it the dialog opens the target's page. */
  onMerged?: (targetId: string) => void;
  /** The city's areas, when the caller already holds them. Without it the dialog loads them. */
  cityAreas?: Area[];
}

/** Folds `source` into another area of its city, moving its venues 50 at a time. */
export const MergeAreaDialog = ({
  source,
  open,
  onOpenChange,
  onMerged,
  cityAreas: given,
}: MergeAreaDialogProps) => {
  const repo = useAreaRepository();
  const navigate = useNavigate();
  const reload = useReloadAreas();
  const [loaded, setLoaded] = React.useState<Area[]>([]);
  const [target, setTarget] = React.useState<string>();
  const [progress, setProgress] = React.useState<number>();
  const [error, setError] = React.useState<string>();

  // The city's own documents (Firestore), so a target made a moment ago is offered.
  const repoRef = React.useRef(repo);
  repoRef.current = repo;
  React.useEffect(() => {
    if (!open || given) return;
    let live = true;
    repoRef.current
      .cityAreas(source.city, source.countryCode)
      .then((areas) => live && setLoaded(areas))
      .catch((e: unknown) => {
        if (live) setError(toHumanErrorMessage(e));
      });
    return () => {
      live = false;
    };
  }, [open, given, source.city, source.countryCode]);

  const targets = (given ?? loaded).filter(
    (r) => r.kind === "area" && r.id !== source.id,
  );
  const busy = progress !== undefined;

  const merge = async () => {
    if (!target) return;
    setError(undefined);
    setProgress(source.venueCount);
    try {
      await runBatches(
        async () => {
          const r = await repo.mergeBatch(target, source.id, BATCH);
          return { done: r.movedVenues, remaining: r.remaining };
        },
        (done, remaining) => setProgress(done + remaining),
      );
      reload();
      onOpenChange(false);
      if (onMerged) onMerged(target);
      else navigate(`/areas/edit/${encodeURIComponent(target)}`);
    } catch (e) {
      // A part with child areas, or a stalled batch: the server's words, not a generic toast.
      reload();
      setError(toHumanErrorMessage(e));
    } finally {
      setProgress(undefined);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge {source.name}</DialogTitle>
          <DialogDescription>
            Moves this area's venues and spellings into the area you pick, then
            removes it. Apps pick up changes within 1 hour.
          </DialogDescription>
        </DialogHeader>
        <FilterSelect
          label="Merge into"
          value={target}
          placeholder="Choose an area"
          className="w-full"
          options={targets.map((t) => ({ value: t.id, label: t.name }))}
          disabled={busy}
          onChange={setTarget}
        />
        {target && !busy && (
          <p className="text-sm">
            Move {source.venueCount} venues from {source.name} into{" "}
            {targets.find((t) => t.id === target)?.name}?
          </p>
        )}
        {busy && <p className="text-sm">Moving {progress} venues…</p>}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button disabled={!target || busy} onClick={merge}>
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
